/**
 * AP-PORTAL-01: Supplier PO Detail View
 * Route: /portal/po/:poId (protected)
 *
 * Full PO detail with line items, acknowledgement action, and exception workflow.
 * Suppliers cannot modify PO terms. Acknowledgement is receipt confirmation only.
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  LogOut,
  Package,
  Archive,
  XCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  Plus,
} from "lucide-react";
import { usePortalUser } from "@/components/PortalProtectedRoute";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  ISSUED: { label: "Issued", bg: "#E8F5E9", text: "#2E7D32", border: "#A5D6A7", icon: Package },
  CLOSED: { label: "Closed", bg: "#F5F5F5", text: "#616161", border: "#E0E0E0", icon: Archive },
  CANCELLED: { label: "Cancelled", bg: "#FFEBEE", text: "#C62828", border: "#FFCDD2", icon: XCircle },
};

const REASON_CODES = [
  { value: "PRICE_DISCREPANCY", label: "Price Discrepancy" },
  { value: "SCOPE_MISMATCH", label: "Scope Mismatch" },
  { value: "DELIVERY_TERMS", label: "Delivery Terms" },
  { value: "PAYMENT_TERMS", label: "Payment Terms" },
  { value: "QUANTITY_ERROR", label: "Quantity Error" },
  { value: "TAX_DISCREPANCY", label: "Tax Discrepancy" },
  { value: "OTHER", label: "Other" },
];

function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: "#F5F5F5", text: "#666", border: "#DDD" };
  const Icon = cfg.icon || FileText;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "4px 12px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 600,
        background: cfg.bg,
        color: cfg.text,
        border: `1px solid ${cfg.border}`,
      }}
    >
      <Icon size={12} />
      {cfg.label}
    </span>
  );
}

export default function SupplierPODetail() {
  const { poId } = useParams();
  const navigate = useNavigate();
  const { portalUser, setPortalUser } = usePortalUser();

  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Acknowledgement
  const [acknowledging, setAcknowledging] = useState(false);
  const [ackNotes, setAckNotes] = useState("");
  const [showAckForm, setShowAckForm] = useState(false);

  // Exception
  const [showExceptionForm, setShowExceptionForm] = useState(false);
  const [excReasonCode, setExcReasonCode] = useState("PRICE_DISCREPANCY");
  const [excComments, setExcComments] = useState("");
  const [submittingException, setSubmittingException] = useState(false);

  // Lines accordion
  const [linesExpanded, setLinesExpanded] = useState(true);

  useEffect(() => {
    loadPO();
  }, [poId]);

  async function loadPO() {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/api/portal/po/${poId}`, { withCredentials: true });
      setPo(res.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404 || status === 403) {
        setError("This purchase order was not found or you do not have access to it.");
      } else {
        setError("Failed to load purchase order. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleAcknowledge() {
    setAcknowledging(true);
    try {
      const res = await axios.post(
        `${API}/api/portal/po/${poId}/acknowledge`,
        { notes: ackNotes.trim() || undefined },
        { withCredentials: true }
      );
      setPo((prev) => ({
        ...prev,
        acknowledged: true,
        acknowledgement: res.data,
      }));
      setShowAckForm(false);
      setAckNotes("");
      toast.success("Purchase Order acknowledged successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Acknowledgement failed");
    } finally {
      setAcknowledging(false);
    }
  }

  async function handleRaiseException() {
    if (!excComments.trim()) {
      toast.error("Please describe the exception before submitting");
      return;
    }
    setSubmittingException(true);
    try {
      const res = await axios.post(
        `${API}/api/portal/po/${poId}/exception`,
        { reason_code: excReasonCode, comments: excComments.trim() },
        { withCredentials: true }
      );
      setPo((prev) => ({
        ...prev,
        exceptions: [res.data, ...(prev.exceptions || [])],
      }));
      setShowExceptionForm(false);
      setExcComments("");
      toast.success("Exception raised — Prashasta will review your query");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to raise exception");
    } finally {
      setSubmittingException(false);
    }
  }

  async function handleLogout() {
    try {
      await axios.post(`${API}/api/portal/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    setPortalUser(null);
    navigate("/portal/login", { replace: true });
  }

  if (loading) {
    return (
      <div style={pageStyle}>
        <NavBar portalUser={portalUser} onLogout={handleLogout} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              border: "2px solid #EDE5D8",
              borderTopColor: "#C9A15F",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          />
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !po) {
    return (
      <div style={pageStyle}>
        <NavBar portalUser={portalUser} onLogout={handleLogout} />
        <div style={{ maxWidth: "600px", margin: "80px auto", padding: "0 24px", textAlign: "center" }}>
          <AlertTriangle size={40} style={{ color: "#D97706", marginBottom: "16px" }} />
          <p style={{ fontSize: "15px", color: "#1A1A1A", fontWeight: 600, marginBottom: "8px" }}>
            {error || "Purchase order not found"}
          </p>
          <button onClick={() => navigate("/portal/dashboard")} style={backBtnStyle}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currency = po.currency_code || "INR";

  return (
    <div data-testid="supplier-po-detail" style={pageStyle}>
      <NavBar portalUser={portalUser} onLogout={handleLogout} />

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "32px 24px" }}>
        {/* Back */}
        <button
          data-testid="back-to-dashboard"
          onClick={() => navigate("/portal/dashboard")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "12px",
            color: "#8A7A6A",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "0 0 20px",
          }}
        >
          <ArrowLeft size={14} />
          Back to Purchase Orders
        </button>

        {/* PO Header */}
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #EDE5D8",
            borderRadius: "12px",
            padding: "24px 28px",
            marginBottom: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
            <div>
              <h1
                data-testid="po-number"
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: "clamp(20px, 3vw, 26px)",
                  fontWeight: 600,
                  color: "#1A1A1A",
                  margin: "0 0 4px",
                }}
              >
                {po.po_number || po.internal_draft_ref || "Purchase Order"}
              </h1>
              <p style={{ fontSize: "12px", color: "#9A8A78", margin: 0 }}>
                Issued {fmtDate(po.issued_at)}
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <StatusBadge status={po.status} />
              {po.acknowledged && (
                <span
                  data-testid="ack-badge"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: 600,
                    background: "#E8F5E9",
                    color: "#2E7D32",
                    border: "1px solid #A5D6A7",
                  }}
                >
                  <CheckCircle2 size={11} /> Acknowledged
                </span>
              )}
            </div>
          </div>

          {/* PO Meta Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: "16px",
              paddingTop: "20px",
              borderTop: "1px solid #F0EBE0",
            }}
          >
            <MetaItem label="Event" value={po.event_name_snapshot} testId="po-event" />
            <MetaItem label="Supplier" value={po.supplier_name_snapshot} testId="po-supplier" />
            <MetaItem label="Currency" value={po.currency_code} />
            <MetaItem label="Delivery Start" value={fmtDate(po.delivery_start_date)} />
            <MetaItem label="Delivery End" value={fmtDate(po.delivery_end_date)} />
            {po.notes && <MetaItem label="Notes" value={po.notes} span />}
          </div>
        </div>

        {/* Financial Summary */}
        <div
          style={{
            background: "#1A1A1A",
            borderRadius: "12px",
            padding: "20px 28px",
            marginBottom: "20px",
            display: "flex",
            flexWrap: "wrap",
            gap: "28px",
          }}
        >
          <div>
            <p style={{ fontSize: "11px", color: "#6B5B4A", margin: "0 0 4px", letterSpacing: "0.5px", textTransform: "uppercase" }}>Subtotal</p>
            <p data-testid="po-subtotal" style={{ fontSize: "18px", fontWeight: 600, color: "#F5EFE6", margin: 0 }}>
              {fmt(po.subtotal_minor, currency)}
            </p>
          </div>
          <div>
            <p style={{ fontSize: "11px", color: "#6B5B4A", margin: "0 0 4px", letterSpacing: "0.5px", textTransform: "uppercase" }}>Tax</p>
            <p style={{ fontSize: "18px", fontWeight: 600, color: "#8A7A6A", margin: 0 }}>
              {fmt(po.tax_amount_minor, currency)}
            </p>
          </div>
          <div style={{ borderLeft: "1px solid rgba(255,255,255,0.1)", paddingLeft: "28px" }}>
            <p style={{ fontSize: "11px", color: "#C9A15F", margin: "0 0 4px", letterSpacing: "0.5px", textTransform: "uppercase" }}>Total</p>
            <p data-testid="po-total" style={{ fontSize: "24px", fontWeight: 700, color: "#C9A15F", margin: 0 }}>
              {fmt(po.total_minor, currency)}
            </p>
          </div>
        </div>

        {/* Line Items */}
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #EDE5D8",
            borderRadius: "12px",
            overflow: "hidden",
            marginBottom: "20px",
          }}
        >
          <button
            data-testid="toggle-lines"
            onClick={() => setLinesExpanded(!linesExpanded)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 24px",
              background: "#FAF6F0",
              border: "none",
              cursor: "pointer",
              borderBottom: linesExpanded ? "1px solid #EDE5D8" : "none",
            }}
          >
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A" }}>
              Line Items ({po.line_count || 0})
            </span>
            {linesExpanded ? <ChevronUp size={16} style={{ color: "#9A8A78" }} /> : <ChevronDown size={16} style={{ color: "#9A8A78" }} />}
          </button>

          {linesExpanded && (
            <>
              {/* Header */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 80px 1fr 80px 1fr 1fr",
                  padding: "10px 24px",
                  fontSize: "10px",
                  fontWeight: 600,
                  color: "#9A8A78",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  borderBottom: "1px solid #F0EBE0",
                }}
              >
                <span>Description</span>
                <span style={{ textAlign: "right" }}>Qty</span>
                <span style={{ textAlign: "right" }}>Unit Rate</span>
                <span style={{ textAlign: "right" }}>Tax</span>
                <span style={{ textAlign: "right" }}>Line Total</span>
                <span style={{ textAlign: "right" }}>With Tax</span>
              </div>

              {(po.lines || []).map((line, idx) => (
                <div
                  key={line.id}
                  data-testid={`po-line-${idx}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 80px 1fr 80px 1fr 1fr",
                    padding: "12px 24px",
                    borderBottom: idx < (po.lines.length - 1) ? "1px solid #F9F5EE" : "none",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <p style={{ fontSize: "13px", color: "#1A1A1A", margin: "0 0 2px", fontWeight: 500 }}>
                      {line.description}
                    </p>
                    {line.delivery_milestone && (
                      <p style={{ fontSize: "11px", color: "#9A8A78", margin: 0 }}>
                        Milestone: {line.delivery_milestone}
                      </p>
                    )}
                    {line.tax_code_snapshot?.code && (
                      <p style={{ fontSize: "11px", color: "#B0A090", margin: 0 }}>
                        {line.tax_code_snapshot.code}
                      </p>
                    )}
                  </div>
                  <span style={{ fontSize: "13px", color: "#6B5B4A", textAlign: "right" }}>
                    {line.quantity}
                  </span>
                  <span style={{ fontSize: "13px", color: "#6B5B4A", textAlign: "right" }}>
                    {fmt(line.unit_rate_minor, currency)}
                  </span>
                  <span style={{ fontSize: "12px", color: "#9A8A78", textAlign: "right" }}>
                    {line.tax_rate_bps ? `${(line.tax_rate_bps / 100).toFixed(0)}%` : "—"}
                  </span>
                  <span style={{ fontSize: "13px", color: "#6B5B4A", textAlign: "right" }}>
                    {fmt(line.line_total_minor, currency)}
                  </span>
                  <span style={{ fontSize: "13px", fontWeight: 500, color: "#1A1A1A", textAlign: "right" }}>
                    {fmt(line.line_total_with_tax_minor, currency)}
                  </span>
                </div>
              ))}

              {(po.lines || []).length === 0 && (
                <p style={{ padding: "20px 24px", fontSize: "13px", color: "#9A8A78" }}>
                  No line items on this purchase order.
                </p>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        {po.status === "ISSUED" && (
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #EDE5D8",
              borderRadius: "12px",
              padding: "20px 24px",
              marginBottom: "20px",
            }}
          >
            <p style={{ fontSize: "12px", fontWeight: 600, color: "#9A8A78", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: "16px", margin: "0 0 16px" }}>
              Actions
            </p>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {/* Acknowledge button */}
              {!po.acknowledged && (
                <button
                  data-testid="acknowledge-po-btn"
                  onClick={() => setShowAckForm(!showAckForm)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 20px",
                    background: "#1A1A1A",
                    color: "#F5EFE6",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                >
                  <CheckCircle2 size={15} />
                  Acknowledge Receipt
                </button>
              )}
              {po.acknowledged && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 20px",
                    background: "#F0FDF4",
                    color: "#2E7D32",
                    border: "1px solid #A5D6A7",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  <CheckCircle2 size={15} />
                  Acknowledged {fmtDate(po.acknowledgement?.acknowledged_at)}
                </div>
              )}

              {/* Exception button */}
              <button
                data-testid="raise-exception-btn"
                onClick={() => setShowExceptionForm(!showExceptionForm)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 20px",
                  background: "transparent",
                  color: "#DC2626",
                  border: "1px solid #FECACA",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <AlertTriangle size={14} />
                Raise Exception
              </button>
            </div>

            {/* Acknowledge form */}
            {showAckForm && !po.acknowledged && (
              <div
                data-testid="ack-form"
                style={{
                  marginTop: "16px",
                  padding: "16px",
                  background: "#F9F5EE",
                  borderRadius: "10px",
                  border: "1px solid #EDE5D8",
                }}
              >
                <p style={{ fontSize: "13px", color: "#1A1A1A", margin: "0 0 12px", fontWeight: 500 }}>
                  Confirm receipt of Purchase Order {po.po_number}
                </p>
                <p style={{ fontSize: "12px", color: "#8A7A6A", margin: "0 0 12px" }}>
                  Acknowledgement confirms you have received and reviewed the PO terms. This is not service acceptance or completion.
                </p>
                <textarea
                  data-testid="ack-notes-input"
                  value={ackNotes}
                  onChange={(e) => setAckNotes(e.target.value)}
                  placeholder="Optional notes or reference (e.g., internal reference number)"
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1px solid #D4C5B0",
                    borderRadius: "8px",
                    fontSize: "13px",
                    color: "#1A1A1A",
                    background: "#FFFFFF",
                    outline: "none",
                    boxSizing: "border-box",
                    resize: "vertical",
                    marginBottom: "12px",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    data-testid="confirm-acknowledge-btn"
                    onClick={handleAcknowledge}
                    disabled={acknowledging}
                    style={{
                      padding: "8px 18px",
                      background: acknowledging ? "#C9A15F80" : "#C9A15F",
                      color: "#111010",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: acknowledging ? "not-allowed" : "pointer",
                    }}
                  >
                    {acknowledging ? "Confirming…" : "Confirm Acknowledgement"}
                  </button>
                  <button
                    onClick={() => { setShowAckForm(false); setAckNotes(""); }}
                    style={{
                      padding: "8px 14px",
                      background: "transparent",
                      border: "1px solid #D4C5B0",
                      borderRadius: "6px",
                      fontSize: "13px",
                      color: "#8A7A6A",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Exception form */}
            {showExceptionForm && (
              <div
                data-testid="exception-form"
                style={{
                  marginTop: "16px",
                  padding: "16px",
                  background: "#FFF8F8",
                  borderRadius: "10px",
                  border: "1px solid #FECACA",
                }}
              >
                <p style={{ fontSize: "13px", color: "#1A1A1A", margin: "0 0 12px", fontWeight: 500 }}>
                  Raise an Exception
                </p>
                <p style={{ fontSize: "12px", color: "#8A7A6A", margin: "0 0 14px" }}>
                  Exceptions do not modify PO terms. Prashasta will review and respond to your query.
                </p>

                {/* Reason code */}
                <div style={{ marginBottom: "12px" }}>
                  <label style={{ fontSize: "11px", color: "#9A8A78", display: "block", marginBottom: "6px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                    Reason
                  </label>
                  <select
                    data-testid="exception-reason-select"
                    value={excReasonCode}
                    onChange={(e) => setExcReasonCode(e.target.value)}
                    style={{
                      padding: "9px 14px",
                      border: "1px solid #D4C5B0",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#1A1A1A",
                      background: "#FFFFFF",
                      outline: "none",
                      cursor: "pointer",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {REASON_CODES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {/* Comments */}
                <div style={{ marginBottom: "12px" }}>
                  <label style={{ fontSize: "11px", color: "#9A8A78", display: "block", marginBottom: "6px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                    Details *
                  </label>
                  <textarea
                    data-testid="exception-comments-input"
                    value={excComments}
                    onChange={(e) => setExcComments(e.target.value)}
                    placeholder="Describe the issue in detail — include line numbers, amounts, or reference values where relevant"
                    rows={4}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      border: "1px solid #D4C5B0",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#1A1A1A",
                      background: "#FFFFFF",
                      outline: "none",
                      boxSizing: "border-box",
                      resize: "vertical",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    data-testid="submit-exception-btn"
                    onClick={handleRaiseException}
                    disabled={submittingException || !excComments.trim()}
                    style={{
                      padding: "8px 18px",
                      background: submittingException || !excComments.trim() ? "#DC262640" : "#DC2626",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: submittingException || !excComments.trim() ? "not-allowed" : "pointer",
                    }}
                  >
                    {submittingException ? "Submitting…" : "Submit Exception"}
                  </button>
                  <button
                    onClick={() => { setShowExceptionForm(false); setExcComments(""); setExcReasonCode("PRICE_DISCREPANCY"); }}
                    style={{
                      padding: "8px 14px",
                      background: "transparent",
                      border: "1px solid #D4C5B0",
                      borderRadius: "6px",
                      fontSize: "13px",
                      color: "#8A7A6A",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Exceptions log */}
        {(po.exceptions || []).length > 0 && (
          <div
            data-testid="exceptions-list"
            style={{
              background: "#FFFFFF",
              border: "1px solid #EDE5D8",
              borderRadius: "12px",
              padding: "20px 24px",
              marginBottom: "20px",
            }}
          >
            <p style={{ fontSize: "12px", fontWeight: 600, color: "#9A8A78", letterSpacing: "0.5px", textTransform: "uppercase", margin: "0 0 16px" }}>
              Exceptions ({po.exceptions.length})
            </p>
            {po.exceptions.map((exc, idx) => (
              <div
                key={exc.id}
                data-testid={`exception-${idx}`}
                style={{
                  padding: "14px",
                  border: "1px solid #F0EBE0",
                  borderRadius: "8px",
                  marginBottom: idx < po.exceptions.length - 1 ? "10px" : 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", marginBottom: "8px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#1A1A1A" }}>
                    {REASON_CODES.find((r) => r.value === exc.reason_code)?.label || exc.reason_code}
                  </span>
                  <ExceptionStatusBadge status={exc.status} />
                </div>
                <p style={{ fontSize: "13px", color: "#4A3A2A", margin: "0 0 6px" }}>{exc.comments}</p>
                <p style={{ fontSize: "11px", color: "#9A8A78", margin: 0 }}>
                  Raised {fmtDate(exc.created_at)}
                  {exc.resolution_notes && (
                    <>
                      {" · "}
                      <span style={{ color: "#6B9E6B" }}>
                        Resolution: {exc.resolution_notes}
                      </span>
                    </>
                  )}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ExceptionStatusBadge({ status }) {
  const configs = {
    OPEN: { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A", label: "Open" },
    UNDER_REVIEW: { bg: "#EFF6FF", text: "#1E40AF", border: "#BFDBFE", label: "Under Review" },
    RESOLVED: { bg: "#F0FDF4", text: "#166534", border: "#BBF7D0", label: "Resolved" },
    DISMISSED: { bg: "#F5F5F5", text: "#616161", border: "#E0E0E0", label: "Dismissed" },
  };
  const cfg = configs[status] || { bg: "#F5F5F5", text: "#666", border: "#DDD", label: status };
  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: "10px",
        fontSize: "10px",
        fontWeight: 600,
        background: cfg.bg,
        color: cfg.text,
        border: `1px solid ${cfg.border}`,
        letterSpacing: "0.3px",
      }}
    >
      {cfg.label}
    </span>
  );
}

function NavBar({ portalUser, onLogout }) {
  return (
    <nav
      style={{
        background: "#1A1A1A",
        borderBottom: "1px solid rgba(201,161,95,0.2)",
        padding: "0 24px",
        height: "60px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "18px", fontWeight: 600, color: "#C9A15F" }}>
          Prashasta
        </span>
        <span style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.15)" }} />
        <span style={{ fontSize: "12px", color: "#8A7A6A", letterSpacing: "1px", textTransform: "uppercase" }}>
          Partner Portal
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div style={{ textAlign: "right" }}>
          <p style={{ fontSize: "12px", color: "#F5EFE6", margin: 0, fontWeight: 500 }}>
            {portalUser?.name || portalUser?.email}
          </p>
          <p style={{ fontSize: "11px", color: "#6B5B4A", margin: 0 }}>
            {portalUser?.supplier_name || "Supplier"}
          </p>
        </div>
        <button
          data-testid="portal-logout-btn"
          onClick={onLogout}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "7px 14px",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: "8px",
            color: "#8A7A6A",
            fontSize: "12px",
            cursor: "pointer",
          }}
        >
          <LogOut size={13} />
          Sign out
        </button>
      </div>
    </nav>
  );
}

function MetaItem({ label, value, testId, span }) {
  return (
    <div style={span ? { gridColumn: "1 / -1" } : {}}>
      <p style={{ fontSize: "10px", color: "#B0A090", margin: "0 0 3px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
        {label}
      </p>
      <p data-testid={testId} style={{ fontSize: "13px", color: "#1A1A1A", margin: 0, fontWeight: 500 }}>
        {value || "—"}
      </p>
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh",
  background: "#FBF7F2",
  fontFamily: "'DM Sans', sans-serif",
};

const backBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 20px",
  background: "#1A1A1A",
  color: "#F5EFE6",
  border: "none",
  borderRadius: "8px",
  fontSize: "13px",
  cursor: "pointer",
  marginTop: "16px",
};
